/* Ring Size Converter Tool — Core logic
 * Public API: window.RingSizeConverter.init(rootEl, options)
 *
 * Spec: tools/ifshe/001-ring-size-converter/design-spec.md
 * Data source: ISO 8653:2016 (EU size = inner circumference mm)
 */
(function () {
  'use strict';

  // ---------- DATA: 11-country ring size conversion table ----------
  // Each row = one canonical size. EU value (= inner circumference mm) is the key.
  // Source: ISO 8653:2016 cross-checked with Brilliance.com, Antoanetta, Tiffany, Omni.
  // Half sizes for US are computed by interpolating between adjacent EU values.
  const TABLE = [
    // [innerDiameterMm, circMm, US, UK, EU, France, Germany, Italy, Spain, Australia, Russia, Switzerland]
    [14.0, 44.0, '3',    'F',  '44', '44', '14',   '4',    '4',    'F',  '14',   '4'],
    [14.4, 45.3, '3.5',  'G',  '45', '45', '14¼',  '5',    '5',    'G',  '14.25','5'],
    [14.8, 46.5, '4',    'H',  '47', '47', '14¾',  '6.5',  '6.5',  'H',  '14.75','6.5'],
    [15.2, 47.8, '4.5',  'I',  '48', '48', '15¼',  '7.5',  '7.5',  'I',  '15.25','7.5'],
    [15.6, 49.0, '5',    'J½', '49', '49', '15½',  '9',    '9',    'J½', '15.5', '9'],
    [16.0, 50.3, '5.5',  'K',  '50', '50', '16',   '10',   '10',   'K',  '16',   '10'],
    [16.5, 51.9, '6',    'L',  '52', '52', '16½',  '12',   '12',   'L',  '16.5', '12'],
    [16.9, 53.1, '6.5',  'M',  '53', '53', '16¾',  '13',   '13',   'M',  '16.75','13'],
    [17.3, 54.4, '7',    'N',  '54', '54', '17¼',  '14',   '14',   'N',  '17.25','14'],
    [17.7, 55.7, '7.5',  'O',  '56', '56', '17¾',  '15.5', '15.5', 'O',  '17.75','15.5'],
    [18.1, 56.9, '8',    'P',  '57', '57', '18',   '16.5', '16.5', 'P',  '18',   '16.5'],
    [18.5, 58.1, '8.5',  'Q',  '58', '58', '18½',  '18',   '18',   'Q',  '18.5', '18'],
    [18.9, 59.4, '9',    'R',  '59', '59', '19',   '19',   '19',   'R',  '19',   '19'],
    [19.4, 60.9, '9.5',  'S',  '61', '61', '19¼',  '20.5', '20.5', 'S',  '19.25','20.5'],
    [19.8, 62.2, '10',   'T',  '62', '62', '19¾',  '22',   '22',   'T',  '19.75','22'],
    [20.2, 63.5, '10.5', 'U',  '64', '64', '20¼',  '23.5', '23.5', 'U',  '20.25','23.5'],
    [20.6, 64.7, '11',   'V',  '65', '65', '20¾',  '25',   '25',   'V',  '20.75','25'],
    [21.0, 65.9, '11.5', 'W',  '66', '66', '21¼',  '26',   '26',   'W',  '21.25','26'],
    [21.4, 67.2, '12',   'X',  '67', '67', '21¾',  '27.5', '27.5', 'X',  '21.75','27.5'],
    [21.8, 68.5, '12.5', 'Y',  '69', '69', '22¼',  '29',   '29',   'Y',  '22.25','29'],
    [22.2, 69.7, '13',   'Z',  '70', '70', '22¾',  '30',   '30',   'Z',  '22.75','30'],
  ];
  const COLS = ['mm','circ','us','uk','eu','france','germany','italy','spain','australia','russia','switzerland'];

  // ISO 7810 ID-1 credit card: 85.60mm × 53.98mm
  const CARD_WIDTH_MM = 85.60;
  const CARD_HEIGHT_MM = 53.98;

  // Parse user/table numbers: unicode fractions (German sizes like 17¼),
  // "17 1/4" spellings, and European decimal commas ("17,3").
  function toNum(value) {
    let s = String(value).trim().replace(',', '.');
    s = s.replace(/\s*1\/4/, '.25').replace(/\s*1\/2/, '.5').replace(/\s*3\/4/, '.75');
    s = s.replace(/¼/, '.25').replace(/½/, '.5').replace(/¾/, '.75');
    return parseFloat(s);
  }

  // ---------- CORE: lookup ----------
  function findRowByDiameter(mm) {
    if (mm == null || isNaN(mm)) return null;
    // Snap to nearest row; if outside table range, return null (caller handles)
    if (mm < TABLE[0][0] - 0.2) return null;
    if (mm > TABLE[TABLE.length - 1][0] + 0.2) return null;
    let best = TABLE[0]; let bestDist = Math.abs(mm - TABLE[0][0]);
    for (let i = 1; i < TABLE.length; i++) {
      const d = Math.abs(mm - TABLE[i][0]);
      if (d < bestDist) { best = TABLE[i]; bestDist = d; }
    }
    return rowToObj(best);
  }
  function findRowByCircumference(circ) {
    if (circ == null || isNaN(circ)) return null;
    return findRowByDiameter(circ / Math.PI);
  }
  function findRowBySystem(system, value) {
    if (!value) return null;
    const col = COLS.indexOf(system);
    if (col === -1) return null;
    let target = String(value).trim().toUpperCase();
    // Letter systems: normalize half-size spellings ("J 1/2", "J1/2", "J.5" -> "J½")
    if (system === 'uk' || system === 'australia') {
      target = target.replace(/\s*1\/2$/, '½').replace(/\s*0?\.5$/, '½').replace(/\s+/g, '');
    }
    for (const row of TABLE) {
      if (String(row[col]).trim().toUpperCase() === target) return rowToObj(row);
    }
    // EU/France sizes ARE the inner circumference in mm (continuous scale) —
    // valid sizes like 46/51/55 sit between table rows, so snap by circumference.
    if (system === 'eu' || system === 'france') {
      const circ = toNum(value);
      if (!isNaN(circ)) {
        const byCirc = findRowByCircumference(circ);
        if (byCirc) return byCirc;
      }
    }
    // Other numeric systems: snap to the nearest row within tolerance.
    if (['us', 'germany', 'italy', 'spain', 'russia', 'switzerland'].includes(system)) {
      const tn = toNum(value);
      if (!isNaN(tn)) {
        let best = null; let bestDist = Infinity;
        for (const row of TABLE) {
          const v = toNum(row[col]);
          if (!isNaN(v)) {
            const d = Math.abs(v - tn);
            if (d < bestDist) { bestDist = d; best = row; }
          }
        }
        if (best && bestDist <= 0.5) return rowToObj(best);
      }
    }
    return null;
  }
  function rowToObj(row) {
    const o = {};
    COLS.forEach((c, i) => o[c] = row[i]);
    return o;
  }

  // ---------- GA4 event ----------
  function track(event, params) {
    try {
      if (window.gtag) window.gtag('event', event, params || {});
      else if (window.dataLayer) window.dataLayer.push({ event: event, ...(params || {}) });
    } catch (_) {}
  }

  // ---------- UI Controller ----------
  function init(root, opts) {
    opts = opts || {};
    const locale = (opts.locale || 'en-US');  // 'en-US' or 'en-GB'

    // ----- TAB SWITCHING -----
    const tabs = root.querySelectorAll('[data-rsc-tab]');
    const panels = root.querySelectorAll('[data-rsc-panel]');
    function activateTab(name) {
      tabs.forEach(t => t.classList.toggle('is-active', t.dataset.rscTab === name));
      panels.forEach(p => p.classList.toggle('is-active', p.dataset.rscPanel === name));
      track('tool_tab_view', { tab: name });
    }
    tabs.forEach(t => t.addEventListener('click', () => activateTab(t.dataset.rscTab)));

    // ----- RESULT DISPLAY -----
    const resultEl = root.querySelector('[data-rsc-result]');
    const resultUsEl = root.querySelector('[data-rsc-result-us]');
    const resultUkEl = root.querySelector('[data-rsc-result-uk]');
    const resultEuEl = root.querySelector('[data-rsc-result-eu]');
    const resultFranceEl = root.querySelector('[data-rsc-result-france]');
    const resultGermanyEl = root.querySelector('[data-rsc-result-germany]');
    const resultItalyEl = root.querySelector('[data-rsc-result-italy]');
    const resultSpainEl = root.querySelector('[data-rsc-result-spain]');
    const resultAusEl = root.querySelector('[data-rsc-result-australia]');
    const resultRussiaEl = root.querySelector('[data-rsc-result-russia]');
    const resultSwissEl = root.querySelector('[data-rsc-result-switzerland]');
    const resultDiameterEl = root.querySelector('[data-rsc-result-diameter]');
    const resultCircEl = root.querySelector('[data-rsc-result-circ]');
    const shopCtaEl = root.querySelector('[data-rsc-shop-cta]');
    const shopLinkEl = root.querySelector('[data-rsc-shop-link]');

    function showResult(row, method) {
      if (!row) return;
      resultUsEl.textContent = row.us;
      resultUkEl.textContent = row.uk;
      resultEuEl.textContent = row.eu;
      resultFranceEl.textContent = row.france;
      resultGermanyEl.textContent = row.germany;
      resultItalyEl.textContent = row.italy;
      resultSpainEl.textContent = row.spain;
      resultAusEl.textContent = row.australia;
      resultRussiaEl.textContent = row.russia;
      resultSwissEl.textContent = row.switzerland;
      resultDiameterEl.textContent = row.mm.toFixed(2) + 'mm';
      resultCircEl.textContent = row.circ.toFixed(1) + 'mm';
      resultEl.classList.add('is-visible');
      if (shopCtaEl) {
        shopCtaEl.classList.remove('rsc-hidden');
        if (shopLinkEl) {
          // Note your size: pick size on the product page itself via Live Product Options.
          // Don't filter URL — ifshe ring sizes are app-managed, not Shopify variants.
          shopLinkEl.textContent = 'Shop ifshe rings →';
          shopLinkEl.href = shopLinkEl.dataset.rscCollectionBase || '/collections/all-rings';
        }
      }
      track('tool_size_calculated', { method: method, us_size: row.us, eu_size: row.eu });
      // Scroll into view (after a tick for re-flow)
      setTimeout(() => resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }

    // ----- TAB 1: CONVERT -----
    const convertSystemSel = root.querySelector('[data-rsc-convert-system]');
    const convertValueInput = root.querySelector('[data-rsc-convert-value]');
    const convertBtn = root.querySelector('[data-rsc-convert-btn]');
    const convertErrorEl = root.querySelector('[data-rsc-convert-error]');
    const rangeMsg = locale === 'en-GB'
      ? 'Size not found. Please check your input. Supported range: UK F – Z (US 3 – 13).'
      : 'Size not found. Please check your input. Supported range: US 3 – 13 (UK F – Z).';
    if (convertBtn) {
      convertBtn.addEventListener('click', () => {
        const system = convertSystemSel.value;
        const value = convertValueInput.value.trim();
        if (!value) { convertErrorEl.textContent = 'Please enter your ring size.'; return; }
        let row;
        if (system === 'mm') row = findRowByDiameter(toNum(value));
        else if (system === 'circ') row = findRowByCircumference(toNum(value));
        else row = findRowBySystem(system, value);
        if (!row) {
          // UK half-letter sizes other than J½ sit between two table rows
          const halfLetter = (system === 'uk' || system === 'australia') &&
            /^[A-Za-z]/.test(value) && /(½|1\/2|\.5)\s*$/.test(value);
          convertErrorEl.textContent = halfLetter
            ? 'Half-letter sizes (e.g. L½) sit between two letters. Pick the nearest full letter, or switch to "Inner diameter (mm)" for an exact match.'
            : rangeMsg;
          return;
        }
        convertErrorEl.textContent = '';
        showResult(row, 'convert');
      });
    }

    // ----- TAB 3: PAPER (circumference input) -----
    const paperInput = root.querySelector('[data-rsc-paper-circ]');
    const paperBtn = root.querySelector('[data-rsc-paper-btn]');
    const paperErrorEl = root.querySelector('[data-rsc-paper-error]');
    if (paperBtn) {
      paperBtn.addEventListener('click', () => {
        const v = parseFloat(paperInput.value);
        if (!v || isNaN(v)) { paperErrorEl.textContent = 'Please enter a number in mm.'; return; }
        if (v < 40 || v > 80) { paperErrorEl.textContent = 'Circumference should be 40-80 mm. Did you measure in inches by mistake?'; return; }
        const row = findRowByCircumference(v);
        if (!row) { paperErrorEl.textContent = 'Size out of supported range. Try US 3 – US 13.'; return; }
        paperErrorEl.textContent = '';
        showResult(row, 'paper');
      });
    }

    // ----- TAB 2: PHONE / CREDIT CARD CALIBRATION + SIZER -----
    const calCardFrame = root.querySelector('[data-rsc-cal-card]');
    const calSlider = root.querySelector('[data-rsc-cal-slider]');
    const calStatus = root.querySelector('[data-rsc-cal-status]');
    const sizerArea = root.querySelector('[data-rsc-sizer-area]');
    const sizerCircle = root.querySelector('[data-rsc-sizer-circle]');
    const sizerSlider = root.querySelector('[data-rsc-sizer-slider]');
    const sizerLabel = root.querySelector('[data-rsc-sizer-label]');
    const sizerBtn = root.querySelector('[data-rsc-sizer-btn]');
    const sizerErrorEl = root.querySelector('[data-rsc-sizer-error]');
    const modeRadios = root.querySelectorAll('input[name="rsc-mode"]');

    let pxPerMm = 0;  // calibrated pixels-per-mm for current viewport
    let mode = 'existing';  // 'existing' | 'fit-finger'
    let calMode = 'width';  // 'width' = match card long edge (85.60mm) | 'height' = short edge (53.98mm)
    let calMmTarget = CARD_WIDTH_MM;
    let calibratedWidth = 0;  // window.innerWidth at confirm time (resize/zoom invalidation)

    // Floor at 200px so degenerate viewports can't drive px (and pxPerMm) negative
    function maxFramePx() { return Math.min(1100, Math.max(200, window.innerWidth - 40)); }

    function calSetup() {
      if (!calCardFrame) return;
      // A card's long edge is 85.6mm ≈ 520+ CSS px on phones — physically wider
      // than a portrait phone screen. On narrow touch screens calibrate against
      // the card's SHORT edge (53.98mm) with the card held upright instead.
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      calMode = (coarse && maxFramePx() < 600) ? 'height' : 'width';
      calMmTarget = calMode === 'height' ? CARD_HEIGHT_MM : CARD_WIDTH_MM;
      calCardFrame.style.aspectRatio = calMode === 'height' ? '1 / 1.586' : '1.586 / 1';
      calCardFrame.textContent = calMode === 'height'
        ? 'Hold your card upright (rotated 90°) inside this frame, then drag the slider until the frame matches the card’s SHORT edge.'
        : 'Place your card inside this frame, then drag the slider below until the frame matches your card width.';
    }

    function updateCardFrame() {
      if (!calCardFrame || !calSlider) return;
      const pct = parseInt(calSlider.value, 10) / 100;
      // Range: 60px (slider=0) → up to 1100px or viewport - 40 (slider=100).
      const minPx = 60;
      const px = minPx + pct * (maxFramePx() - minPx);
      calCardFrame.style.width = px + 'px';
      // The card sits INSIDE the frame, so the card spans the frame's inner
      // width: subtract the 2 × 3px dashed border (box-sizing: border-box).
      pxPerMm = (px - 6) / calMmTarget;
      calStatus.textContent = calMode === 'height'
        ? 'Calibrating... drag until the frame exactly matches your card’s short edge (card held upright).'
        : 'Calibrating... drag the slider until the frame exactly matches your real card width.';
      calStatus.classList.remove('is-calibrated');
    }
    let lastConfirmedVal = -1;
    function confirmCalibration() {
      if (pxPerMm <= 0 || !calSlider) return;
      const v = parseInt(calSlider.value, 10);
      if (v === lastConfirmedVal && calStatus.classList.contains('is-calibrated')) return;
      if (v >= 99) {
        // Slider pegged at max: the frame most likely still doesn't reach card size.
        calStatus.textContent = calMode === 'height'
          ? 'Frame still smaller than your card? Rotate your phone to landscape and recalibrate.'
          : 'Frame still smaller than your card? Use the "Measure at home" tab instead.';
        calStatus.classList.remove('is-calibrated');
        return;
      }
      if (v <= 1) return;  // frame near minimum — not a plausible card match
      lastConfirmedVal = v;
      calibratedWidth = window.innerWidth;
      calStatus.textContent = 'Calibrated: frame = ' + calMmTarget + 'mm (1 mm = ' + pxPerMm.toFixed(2) + 'px).';
      calStatus.classList.add('is-calibrated');
      const sizerBlock = sizerArea ? sizerArea.closest('.rsc-sizer') : null;
      if (sizerBlock) sizerBlock.classList.add('is-visible');
      track('tool_calibration_complete', { device_type: window.matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop', cal_mode: calMode });
      updateSizerCircle();
    }
    if (calSlider) {
      calSlider.addEventListener('input', updateCardFrame);
      calSlider.addEventListener('change', () => setTimeout(confirmCalibration, 200));
      // 'change' doesn't fire if the user releases at the starting value —
      // also confirm on release/keyup so calibration can't get stuck.
      calSlider.addEventListener('pointerup', () => setTimeout(confirmCalibration, 200));
      calSlider.addEventListener('touchend', () => setTimeout(confirmCalibration, 200));
      calSlider.addEventListener('keyup', () => setTimeout(confirmCalibration, 200));
    }
    modeRadios.forEach(r => r.addEventListener('change', () => {
      mode = root.querySelector('input[name="rsc-mode"]:checked').value;
      updateSizerCircle();
    }));

    function updateSizerCircle() {
      if (!sizerCircle || !sizerSlider || pxPerMm <= 0) return;
      const mm = parseFloat(sizerSlider.value);
      const px = mm * pxPerMm;
      sizerCircle.style.width = px + 'px';
      sizerCircle.style.height = px + 'px';
      if (sizerLabel) sizerLabel.textContent = mode === 'existing'
        ? 'Adjust until circle matches your ring inner edge: ' + mm.toFixed(1) + 'mm'
        : 'Virtual ring at actual size: ' + mm.toFixed(1) + 'mm diameter';
    }
    if (sizerSlider) sizerSlider.addEventListener('input', updateSizerCircle);
    if (sizerBtn) {
      sizerBtn.addEventListener('click', () => {
        if (pxPerMm <= 0) { sizerErrorEl.textContent = 'Please calibrate using your credit card first.'; return; }
        const mm = parseFloat(sizerSlider.value);
        const row = findRowByDiameter(mm);
        if (!row) { sizerErrorEl.textContent = 'Diameter out of supported range.'; return; }
        sizerErrorEl.textContent = '';
        showResult(row, 'phone');
      });
    }

    // Calibration is only valid while 1 CSS px maps to the same physical size —
    // rotation, window resize and pinch/browser zoom all break that mapping.
    function invalidateCalibration(msg) {
      if (pxPerMm > 0 && calStatus) {
        calStatus.textContent = msg;
        calStatus.classList.remove('is-calibrated');
      }
      pxPerMm = 0;
      lastConfirmedVal = -1;
      calSetup();
    }
    window.addEventListener('orientationchange', () => invalidateCalibration('Screen rotated. Please recalibrate.'));
    window.addEventListener('resize', () => {
      if (pxPerMm > 0 && window.innerWidth !== calibratedWidth) {
        invalidateCalibration('Window size changed. Please recalibrate.');
      }
    });
    if (window.visualViewport) {
      let lastScale = window.visualViewport.scale;
      window.visualViewport.addEventListener('resize', () => {
        if (Math.abs(window.visualViewport.scale - lastScale) > 0.01) {
          lastScale = window.visualViewport.scale;
          if (pxPerMm > 0) invalidateCalibration('Zoom changed. Reset zoom (100%) and recalibrate.');
        }
      });
    }

    // Initial state: pick calibration mode for this screen, and sync the slider
    // position to the frame's CSS start width (200px) so the first touch doesn't jump.
    calSetup();
    if (calSlider && calCardFrame) {
      const initPct = Math.round(((200 - 60) / Math.max(1, maxFramePx() - 60)) * 100);
      calSlider.value = String(Math.max(0, Math.min(100, initPct)));
    }

    // ----- PDF download form -----
    const pdfForm = root.querySelector('[data-rsc-pdf-form]');
    if (pdfForm) {
      pdfForm.addEventListener('submit', () => {
        track('tool_pdf_download_request', { channel: 'email' });
        // Form submits via standard Shopify customer form (handled by theme)
      });
    }
    root.querySelectorAll('[data-rsc-pdf-link]').forEach(a => {
      a.addEventListener('click', () => {
        track('tool_pdf_download_request', { channel: 'direct', format: a.dataset.rscPdfLink });
      });
    });

    // ----- SHOP CTA tracking -----
    if (shopLinkEl) {
      shopLinkEl.addEventListener('click', () => {
        const us = resultUsEl ? resultUsEl.textContent : '';
        track('tool_shop_cta_click', { target_size: us });
      });
    }
  }

  window.RingSizeConverter = { init: init, TABLE: TABLE };
})();
